/**
 * How a chart's Data dialog lays out its two tables — which columns show, in
 * what order, and how the rows sort — saved with the chart's view.
 *
 * Every Market Intelligence view owns one (`dataTable` in `view-config.ts`),
 * so the dialog's layout rides the same lifecycle as the chart's filters: the
 * unsaved dot, Update this view, Save as new, Reset, and the per-user restore
 * when the reader comes back. The dialog changes no filter; this is the whole
 * of what it can change.
 *
 * Columns are kept by id. A table's columns can change under a saved layout —
 * the Data table has one column per line on the chart, and a filter adds or
 * drops lines — so the layout never assumes its ids are all present:
 *
 * - `order` holds ids in the reader's order. A column it does not name sits
 *   after the ones it does, in the table's own order; an id the table no longer
 *   has is kept, so switching a line off and on again restores its place.
 * - `hidden` holds the ids switched off, so a column the layout has never met
 *   (a newly compared point) shows by default.
 */

export type SortDir = "asc" | "desc";

/** A table's sort: one column, one direction. */
export interface TableSort {
  column: string;
  dir: SortDir;
}

/** One table's layout. All empty is the table as it ships. */
export interface TablePrefs {
  order: readonly string[];
  hidden: readonly string[];
  /** `null` is the table's own order (series as charted; newest date first). */
  sort: TableSort | null;
}

/** The Data dialog's two tables. */
export interface DataTablePrefs {
  statistics: TablePrefs;
  data: TablePrefs;
}

export const EMPTY_TABLE_PREFS: TablePrefs = { order: [], hidden: [], sort: null };

export const DEFAULT_DATA_TABLE: DataTablePrefs = {
  statistics: EMPTY_TABLE_PREFS,
  data: EMPTY_TABLE_PREFS,
};

function isEmpty(prefs: TablePrefs): boolean {
  return prefs.order.length === 0 && prefs.hidden.length === 0 && prefs.sort === null;
}

/** True when neither table has been changed — what a view stores nothing for. */
export function isDefaultDataTable(prefs: DataTablePrefs): boolean {
  return isEmpty(prefs.statistics) && isEmpty(prefs.data);
}

// ── Stored form ───────────────────────────────────────────────────────────────

function idsFrom(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((v): v is string => typeof v === "string" && v !== ""))];
}

function sortFrom(raw: unknown): TableSort | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { column, dir } = raw as { column?: unknown; dir?: unknown };
  if (typeof column !== "string" || column === "") return null;
  return dir === "asc" || dir === "desc" ? { column, dir } : null;
}

function tableFrom(raw: unknown): TablePrefs {
  if (typeof raw !== "object" || raw === null) return EMPTY_TABLE_PREFS;
  const r = raw as Record<string, unknown>;
  return { order: idsFrom(r.order), hidden: idsFrom(r.hidden), sort: sortFrom(r.sort) };
}

/**
 * A saved layout, read defensively: a stored view is not trusted input (it may
 * predate a column, or have been edited by hand), so anything malformed
 * degrades to the table as it ships rather than breaking the dialog.
 */
export function parseDataTablePrefs(raw: unknown): DataTablePrefs {
  if (typeof raw !== "object" || raw === null) return DEFAULT_DATA_TABLE;
  const r = raw as Record<string, unknown>;
  return { statistics: tableFrom(r.statistics), data: tableFrom(r.data) };
}

function tableToStored(prefs: TablePrefs): Record<string, unknown> | undefined {
  if (isEmpty(prefs)) return undefined;
  const out: Record<string, unknown> = {};
  if (prefs.order.length > 0) out.order = [...prefs.order];
  if (prefs.hidden.length > 0) out.hidden = [...prefs.hidden];
  if (prefs.sort) out.sort = { column: prefs.sort.column, dir: prefs.sort.dir };
  return out;
}

/**
 * The stored object, in a fixed key order (the dirty check compares JSON
 * strings), with untouched tables left out. `undefined` when both are.
 */
export function dataTableToStored(prefs: DataTablePrefs): Record<string, unknown> | undefined {
  const statistics = tableToStored(prefs.statistics);
  const data = tableToStored(prefs.data);
  if (!statistics && !data) return undefined;
  const out: Record<string, unknown> = {};
  if (statistics) out.statistics = statistics;
  if (data) out.data = data;
  return out;
}

// ── Columns ───────────────────────────────────────────────────────────────────

/**
 * Every id in display order: the saved order (ids the table no longer has
 * included, so they keep their place), then the table's other columns in its
 * own order.
 */
export function columnOrder(columns: readonly string[], prefs: TablePrefs): string[] {
  const saved = new Set(prefs.order);
  return [...prefs.order, ...columns.filter((id) => !saved.has(id))];
}

/**
 * The columns to draw, in order. Never none: a layout that hides every column
 * the table has now shows them all rather than an empty table.
 */
export function visibleColumns(columns: readonly string[], prefs: TablePrefs): string[] {
  const present = new Set(columns);
  const hidden = new Set(prefs.hidden);
  const shown = columnOrder(columns, prefs).filter((id) => present.has(id) && !hidden.has(id));
  return shown.length > 0 ? shown : [...columns];
}

/**
 * The layout after the Columns popover hands back its state (`order` and
 * `visible`, both over every id). An order that matches the table's own is
 * stored as none, so putting the columns back clears the unsaved dot.
 */
export function withColumns(
  columns: readonly string[],
  prefs: TablePrefs,
  next: { order: readonly string[]; visible: readonly string[] },
): TablePrefs {
  const visible = new Set(next.visible);
  const asShipped =
    next.order.length === columns.length && next.order.every((id, i) => id === columns[i]);
  return {
    ...prefs,
    order: asShipped ? [] : [...next.order],
    hidden: next.order.filter((id) => !visible.has(id)),
  };
}

// ── Sorting ───────────────────────────────────────────────────────────────────

/**
 * The sort after a click on `column`'s header: ascending, then descending,
 * then the table's own order again. The table's own order (`defaultSort` —
 * the Data table's newest date first) is stored as none, and on its own
 * column the clicks just flip it.
 */
export function nextSort(
  current: TableSort | null,
  column: string,
  defaultSort: TableSort | null = null,
): TableSort | null {
  const shown = current ?? defaultSort;
  let next: TableSort | null;
  if (!shown || shown.column !== column) next = { column, dir: "asc" };
  else if (shown.dir === "asc") next = { column, dir: "desc" };
  else next = defaultSort?.column === column ? { column, dir: "asc" } : null;
  const isDefault =
    next !== null && defaultSort !== null && next.column === defaultSort.column && next.dir === defaultSort.dir;
  return isDefault ? null : next;
}

/**
 * `rows` sorted by `sort` (or returned as they are for `null`). Empty values go
 * last in either direction — a missing settlement is not the lowest price.
 * Stable, so rows that tie keep the table's own order.
 */
export function sortRows<T>(
  rows: readonly T[],
  sort: TableSort | null,
  valueOf: (row: T, column: string) => number | string | null | undefined,
): T[] {
  if (!sort) return [...rows];
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: valueOf(row, sort.column) }))
    .sort((a, b) => {
      const an = a.v === null || a.v === undefined;
      const bn = b.v === null || b.v === undefined;
      if (an || bn) return an === bn ? a.i - b.i : an ? 1 : -1;
      const cmp =
        typeof a.v === "number" && typeof b.v === "number"
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v));
      return cmp === 0 ? a.i - b.i : sign * cmp;
    })
    .map((x) => x.row);
}
